import React, { useState, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'

import { ReactComponent as CheckIcon } from 'images/icons/check-grey.svg'
import { ReactComponent as PlusIcon } from 'images/icons/plus-icon.svg'
import ProgressBar from '../ProgressBar.component'
import { useUploadButtonsService } from './UploadButtons.service'
import { TrashIcon, EditButtonIcon } from 'images'
import useOnClickOutside from 'hooks/UseOnClickOutside'
import { useMediaQuery } from 'hooks/useMediaQuery'

const UploadButtons = (props) => {
  const { 
    feedButtonFilled, 
    reelButtonFilled, 
    showFeedProgress, 
    showReelProgress, 
    progress, 
    readonly,
    handleFileSelect,
    fileId,
    feedFileId,
    reelFileId,
    canRemove,
    onRemoveFromGroup,
    setNoDelete,
  } = props

  const [openOverlay, setOpenOverlay] = useState(null)
  const buttonsContainerRef = useRef(null)
  const isTouchDevice = useMediaQuery('(hover: none)')

  const { handleButtonClick, onFileInputChange, fileInputRef } = useUploadButtonsService({ feedButtonFilled, reelButtonFilled, feedFileId, reelFileId, fileId, handleFileSelect })
  
  useOnClickOutside(buttonsContainerRef, useCallback(() => {
    setOpenOverlay(null)
  }, []))
  
  const hasBothPlacements = !!(feedFileId && reelFileId)
  const canRemoveMedia = canRemove ? true : hasBothPlacements
  const isFeedButtonDisabled = showReelProgress || readonly
  const isReelButtonDisabled = showFeedProgress || readonly
  
  const closeOverlay = useCallback(() => setOpenOverlay(null), [])

  const handleRemoveFromGroup = (e, fileIdToRemove) => {
    e.stopPropagation()
    e.preventDefault()

    if (canRemoveMedia) {
      setNoDelete(null)
      onRemoveFromGroup?.(fileIdToRemove)
      closeOverlay()
    } else {
      if (fileIdToRemove === feedFileId) {
        setNoDelete({
          mediaType: 'Reel',
          mediaTypeToRemove: 'Feed',
        })
      } else if (fileIdToRemove === reelFileId) {
        setNoDelete({
          mediaType: 'Feed',
          mediaTypeToRemove: 'Reel',
        })
      } else {
        setNoDelete(null)
      }
    }
  }

  const handleFeedButtonClick = (e) => {
    if (isTouchDevice && feedButtonFilled) {
      e.preventDefault()
      e.stopPropagation()
      setOpenOverlay((prev) => (prev === 'feed' ? null : 'feed'))
      return
    }
    setNoDelete(null)
    handleButtonClick(e, 'feed')
  }

  const handleReelButtonClick = (e) => {
    if (isTouchDevice && reelButtonFilled) {
      e.preventDefault()
      e.stopPropagation()
      setOpenOverlay((prev) => (prev === 'reel' ? null : 'reel'))
      return
    }
    setNoDelete(null)
    handleButtonClick(e, 'reel')
  }

  // return
  return (
    <div ref={buttonsContainerRef} className="accepted-file__add-media-buttons">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg, image/png, video/*"
        onChange={onFileInputChange}
        style={{ display: 'none' }}
      />
      
      {showFeedProgress ? (
        <div className="accepted-file__progress-container">
          <ProgressBar progress={progress} />
        </div>
      ) : (
        <div
          className={classnames('accepted-file__add-media-button-wrapper', {
            'accepted-file__add-media-button-wrapper--filled': feedButtonFilled && !readonly,
            'accepted-file__add-media-button-wrapper--overlay-open': openOverlay === 'feed' && !readonly,
          })}
        >
          <div
            className="accepted-file__add-media-button__overlay"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (isTouchDevice) {
                closeOverlay()
              }
            }}
          >
            {onRemoveFromGroup && feedFileId && (
              <div className="d-flex gap-1">
                <span
                  className="action-btn action-btn__edit"
                  onClick={(e) => {
                    setNoDelete(null)
                    handleButtonClick(e, 'feed')
                    closeOverlay()
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <EditButtonIcon width={14} height={14}/>
                </span>

                <span
                  className="action-btn action-btn__remove"
                  onClick={(e) => handleRemoveFromGroup(e, feedFileId)}
                  role="button"
                  tabIndex={0}
                >
                  <TrashIcon width={14} height={14}/>
                </span>
              </div>

            )}
          </div>

          <button
            onClick={handleFeedButtonClick}
            className={classnames('accepted-file__add-media-button', {
              'accepted-file__add-media-button__empty': !feedButtonFilled,
              'accepted-file__add-media-button__disabled': isFeedButtonDisabled,
            })}
            disabled={isFeedButtonDisabled}
          >
            {feedButtonFilled ? (
              <span className="accepted-file__add-media-button__check-icon"><CheckIcon width={14} height={14} /></span>
            ) : (
              <span className="accepted-file__add-media-button__plus-icon"><PlusIcon width={8} height={8} /></span>
            )}

            <span>Feed</span>
          </button>
        </div>
      )}

      {showReelProgress ? (
        <div className="accepted-file__progress-container">
          <ProgressBar progress={progress} />
        </div>
      ) : (
        <div
          className={classnames('accepted-file__add-media-button-wrapper', {
            'accepted-file__add-media-button-wrapper--filled': reelButtonFilled && !readonly,
            'accepted-file__add-media-button-wrapper--overlay-open': openOverlay === 'reel' && !readonly,
          })}
        >
          <div
            className="accepted-file__add-media-button__overlay"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (isTouchDevice) {
                closeOverlay()
              }
            }}
          >
            {onRemoveFromGroup && reelFileId && (
              <div className="d-flex gap-1">
                <span
                  className="action-btn action-btn__edit"
                  onClick={(e) => {
                    setNoDelete(null)
                    handleButtonClick(e, 'reel')
                    closeOverlay()
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <EditButtonIcon width={14} height={14}/>
                </span>

                <span
                  className="action-btn action-btn__remove"
                  onClick={(e) => handleRemoveFromGroup(e, reelFileId)}
                  role="button"
                  tabIndex={0}
                >
                  <TrashIcon width={14} height={14}/>
                </span>   
              </div>
            )}
          </div>
          
          <button
            onClick={handleReelButtonClick}
            className={classnames('accepted-file__add-media-button', {
              'accepted-file__add-media-button__empty': !reelButtonFilled,
              'accepted-file__add-media-button__disabled': isReelButtonDisabled,
            })}
            disabled={isReelButtonDisabled}
          >
            {reelButtonFilled ? (
              <span className="accepted-file__add-media-button__check-icon"><CheckIcon width={14} height={14} /></span>
            ) : (
              <span className="accepted-file__add-media-button__plus-icon"><PlusIcon width={8} height={8} /></span>
            )}

            <span>Reel</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default UploadButtons

UploadButtons.propTypes = {
  feedButtonFilled: PropTypes.bool.isRequired,
  reelButtonFilled: PropTypes.bool.isRequired,
  handleFileSelect: PropTypes.func.isRequired,
  fileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  feedFileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reelFileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showFeedProgress: PropTypes.bool,
  showReelProgress: PropTypes.bool,
  progress: PropTypes.number,
  readonly: PropTypes.bool,
  canRemove: PropTypes.bool,
  onRemoveFromGroup: PropTypes.func,
  setNoDelete: PropTypes.func,
}